import type { CMSLinkableCollection } from '@/lib/i18n/config'
import { getLocaleFromPathname, stripLocaleFromPathname } from '@/lib/i18n/config'
import { getCanonicalLocationSlug } from '@/lib/locations/legacyLocationSlugs'
import {
  FALLBACK_SLUG_TO_TEMPLATE_KEY,
  getFallbackTemplatePath,
  type FixedPageRouteKey,
} from '@/lib/pages/fixedPageRoutes'
import type { TemplatePathMap } from '@/lib/pages/pageTemplates'
import { isArchiveTemplateKey, isDetailArchiveTemplateKey } from '@/lib/pages/pageTemplates'

export type AdminBarCollection = CMSLinkableCollection

export type AdminBarTarget = {
  collection: AdminBarCollection
  labels: {
    plural: string
    singular: string
  }
  locale?: string
  query: Record<string, string>
}

const COLLECTION_LABELS: Record<AdminBarCollection, AdminBarTarget['labels']> = {
  pages: { plural: 'Pages', singular: 'Page' },
  posts: { plural: 'Posts', singular: 'Post' },
  promotions: { plural: 'Promotions', singular: 'Promotion' },
  cities: { plural: 'Cities', singular: 'City' },
  locations: { plural: 'Locations', singular: 'Location' },
  apartments: { plural: 'Apartments', singular: 'Apartment' },
}

const UNSUPPORTED_ROUTE_PREFIXES = ['city', 'location', 'apartment'] as const

function buildTarget(
  collection: AdminBarCollection,
  query: Record<string, string>,
  locale?: string,
): AdminBarTarget {
  return {
    collection,
    labels: COLLECTION_LABELS[collection],
    locale,
    query,
  }
}

function templateKeyFromSegment(
  segment: string,
  templatePaths?: Partial<TemplatePathMap> | null,
): FixedPageRouteKey | undefined {
  if (templatePaths) {
    for (const [key, path] of Object.entries(templatePaths)) {
      const slug = path.replace(/^\//, '')
      if (slug === segment || (key === 'home' && path === '/')) {
        return key as FixedPageRouteKey
      }
    }
  }

  return FALLBACK_SLUG_TO_TEMPLATE_KEY[segment]
}

/**
 * Maps a public frontend pathname to the Payload collection and slug keys
 * needed to resolve the exact CMS document for the admin bar edit link.
 */
export function resolveAdminBarTarget(
  pathname: string,
  templatePaths?: Partial<TemplatePathMap> | null,
): AdminBarTarget | null {
  const locale = getLocaleFromPathname(pathname)
  const basePath = stripLocaleFromPathname(pathname).replace(/\/+$/, '') || '/'
  const segments = basePath.split('/').filter(Boolean)

  if (
    segments[0] &&
    UNSUPPORTED_ROUTE_PREFIXES.includes(segments[0] as (typeof UNSUPPORTED_ROUTE_PREFIXES)[number])
  ) {
    return null
  }

  if (basePath === '/') {
    return buildTarget('pages', { templateKey: 'home' }, locale)
  }

  if (segments[0] === 'cities' && segments[1]) {
    const citySlug = decodeURIComponent(segments[1])

    if (segments[2] === 'apartments' && segments[3]) {
      return null
    }

    if (segments[3] === 'apartments' && segments[4]) {
      return buildTarget(
        'apartments',
        {
          apartmentSlug: decodeURIComponent(segments[4]),
          citySlug,
          locationSlug: getCanonicalLocationSlug(decodeURIComponent(segments[2])),
        },
        locale,
      )
    }

    if (segments[2]) {
      return buildTarget(
        'locations',
        {
          citySlug,
          locationSlug: getCanonicalLocationSlug(decodeURIComponent(segments[2])),
        },
        locale,
      )
    }

    return buildTarget('cities', { slug: citySlug }, locale)
  }

  const first = decodeURIComponent(segments[0] ?? '')
  const templateKey = templateKeyFromSegment(first, templatePaths)

  if (templateKey === 'home') {
    return buildTarget('pages', { templateKey: 'home' }, locale)
  }

  if (isDetailArchiveTemplateKey(templateKey) && segments[1] && segments[1] !== 'page') {
    return buildTarget(templateKey, { slug: decodeURIComponent(segments[1]) }, locale)
  }

  if (isArchiveTemplateKey(templateKey)) {
    return buildTarget('pages', { templateKey }, locale)
  }

  if (templateKey) {
    return buildTarget('pages', { templateKey }, locale)
  }

  if (segments.length === 1) {
    return buildTarget('pages', { slug: first }, locale)
  }

  if (segments.length === 2 && segments[1] !== 'page') {
    const fallbackArchive = getFallbackTemplatePath('posts').replace(/^\//, '')
    const promotionsArchive = getFallbackTemplatePath('promotions').replace(/^\//, '')
    if (first === fallbackArchive) {
      return buildTarget('posts', { slug: decodeURIComponent(segments[1]) }, locale)
    }
    if (first === promotionsArchive) {
      return buildTarget('promotions', { slug: decodeURIComponent(segments[1]) }, locale)
    }
  }

  return null
}

export function buildAdminBarDocumentQuery(target: AdminBarTarget): URLSearchParams {
  const params = new URLSearchParams({
    depth: '0',
    limit: '1',
  })

  if (target.locale) {
    params.set('locale', target.locale)
    params.set('fallbackLocale', 'false')
  }

  const { collection, query } = target

  if (query.templateKey) {
    params.append('where[templateKey][equals]', query.templateKey)
    return params
  }

  if (
    collection === 'pages' ||
    collection === 'posts' ||
    collection === 'promotions' ||
    collection === 'cities'
  ) {
    params.append('where[slug][equals]', query.slug)
    return params
  }

  if (collection === 'locations') {
    params.append('where[and][0][slug][equals]', query.locationSlug)
    params.append('where[and][1][city.slug][equals]', query.citySlug)
    return params
  }

  params.append('where[and][0][slug][equals]', query.apartmentSlug)
  params.append('where[and][1][location.slug][equals]', query.locationSlug)
  params.append('where[and][2][location.city.slug][equals]', query.citySlug)

  return params
}

export function getAdminBarLocale(pathname: string) {
  return getLocaleFromPathname(pathname)
}
